import React, { useState, useEffect, useCallback } from "react";
import {
  X,
  Save,
  Loader2,
  Upload,
  Move3D,
  Info,
  Plus,
} from "lucide-react";
import { productService } from "../../lib/productService";
import { toast } from "sonner";

// Transform database marketing content (snake_case) to frontend format (camelCase)
const transformMarketingContentFromDb = (dbContent) => {
  if (!dbContent) {
    return {
      productAssets: {
        productMockupsUrl: "",
        rawUgcContentEnabled: false,
        trainingDocumentsUrl: "",
      },
      topPerformingExamples: {
        topImageAds: [],
        topVideoAds: [],
        imageLibraryUrl: "",
        videoLibraryUrl: "",
        videoAdsEnabled: false,
      },
      highPerformanceCreatives: {
        videos: [],
        creativeLibraryUrl: "",
      },
      knowYourCustomer: {
        targetDemographics: [],
        customerPersona: {
          name: "",
          age: "",
          location: "",
          income: "",
          jobTitle: "",
          reality: "",
          fears: "",
          hook: "",
        },
      },
      painPointsGrid: [],
      powerClaims: [],
      contentToolsAccess: {
        recharmUgcLibrary: {
          enabled: false,
          instructions: {
            websiteUrl: "https://app.recharm.com/auth",
            email: "ched@mailinator.com",
            inboxUrl: "https://mailinator.com",
          },
          description:
            "Access to 150+ verified UGC clips from real customers. Content updated weekly with fresh testimonials and usage videos for male enhancement and energy benefits.",
        },
        researchAnalysis: {
          kaloDataTraining: {
            title: "Kalo Data Training",
            description:
              "Learn to analyze trending content and competitor strategies for male enhancement and natural energy supplement niches.",
            comingSoon: true,
          },
          competitorIntelligence: {
            title: "Competitor Intelligence",
            description:
              "Study what's working for other honey and male enhancement brands. Updated weekly with new findings.",
            comingSoon: true,
          },
        },
      },
    };
  }

  return {
    productAssets: {
      productMockupsUrl: dbContent.product_assets?.product_mockups_url || "",
      rawUgcContentEnabled:
        dbContent.product_assets?.raw_ugc_content_enabled || false,
      trainingDocumentsUrl:
        dbContent.product_assets?.training_documents_url || "",
    },
    topPerformingExamples: {
      topImageAds: (dbContent.top_performing_examples?.top_image_ads || []).map(
        (ad) => ({
          imageUrl: ad.imageUrl || ad.image_url || "",
          title: ad.title || "",
          spend: ad.spend || 0,
          roas: ad.roas || 0,
        })
      ),
      topVideoAds: (dbContent.top_performing_examples?.top_video_ads || []).map(
        (ad) => ({
          videoUrl: ad.videoUrl || ad.video_url || "",
          title: ad.title || "",
          spend: ad.spend || 0,
          roas: ad.roas || 0,
        })
      ),
      imageLibraryUrl:
        dbContent.top_performing_examples?.image_library_url || "",
      videoLibraryUrl:
        dbContent.top_performing_examples?.video_library_url || "",
      videoAdsEnabled:
        dbContent.top_performing_examples?.video_ads_enabled || false,
    },
    highPerformanceCreatives: {
      videos: (dbContent.high_performance_creatives?.videos || []).map(
        (video) => ({
          videoUrl: video.videoUrl || video.video_url || "",
          title: video.title || "",
          description: video.description || "",
          productionNotes: {
            aiVoice:
              video.production_notes?.ai_voice ||
              video.productionNotes?.aiVoice ||
              false,
            ugcClips:
              video.production_notes?.ugc_clips ||
              video.productionNotes?.ugcClips ||
              false,
            bRoll:
              video.production_notes?.b_roll ||
              video.productionNotes?.bRoll ||
              false,
            aiDeepFake:
              video.production_notes?.ai_deep_fake ||
              video.productionNotes?.aiDeepFake ||
              false,
            productBRoll:
              video.production_notes?.product_b_roll ||
              video.productionNotes?.productBRoll ||
              false,
            customNotes:
              video.production_notes?.custom_notes ||
              video.productionNotes?.customNotes ||
              [],
          },
        })
      ),
      creativeLibraryUrl:
        dbContent.high_performance_creatives?.creative_library_url || "",
    },
    knowYourCustomer: {
      targetDemographics:
        dbContent.know_your_customer?.target_demographics || [],
      customerPersona: {
        name: dbContent.know_your_customer?.customer_persona?.name || "",
        age: dbContent.know_your_customer?.customer_persona?.age || "",
        location:
          dbContent.know_your_customer?.customer_persona?.location || "",
        income: dbContent.know_your_customer?.customer_persona?.income || "",
        jobTitle:
          dbContent.know_your_customer?.customer_persona?.job_title || "",
        reality: dbContent.know_your_customer?.customer_persona?.reality || "",
        fears: dbContent.know_your_customer?.customer_persona?.fears || "",
        hook: dbContent.know_your_customer?.customer_persona?.hook || "",
      },
    },
    painPointsGrid: dbContent.pain_points_grid || [],
    powerClaims: dbContent.power_claims || [],
    contentToolsAccess: {
      recharmUgcLibrary: {
        enabled:
          dbContent.content_tools_access?.recharm_ugc_library?.enabled || false,
        instructions: {
          websiteUrl:
            dbContent.content_tools_access?.recharm_ugc_library?.instructions
              ?.website_url || "https://app.recharm.com/auth",
          email:
            dbContent.content_tools_access?.recharm_ugc_library?.instructions
              ?.email || "ched@mailinator.com",
          inboxUrl:
            dbContent.content_tools_access?.recharm_ugc_library?.instructions
              ?.inbox_url || "https://mailinator.com",
        },
        description:
          dbContent.content_tools_access?.recharm_ugc_library?.description ||
          "Access to 150+ verified UGC clips from real customers. Content updated weekly with fresh testimonials and usage videos for male enhancement and energy benefits.",
      },
      researchAnalysis: {
        kaloDataTraining: {
          title:
            dbContent.content_tools_access?.research_analysis
              ?.kalo_data_training?.title || "Kalo Data Training",
          description:
            dbContent.content_tools_access?.research_analysis
              ?.kalo_data_training?.description ||
            "Learn to analyze trending content and competitor strategies for male enhancement and natural energy supplement niches.",
          comingSoon:
            dbContent.content_tools_access?.research_analysis
              ?.kalo_data_training?.coming_soon ?? true,
        },
        competitorIntelligence: {
          title:
            dbContent.content_tools_access?.research_analysis
              ?.competitor_intelligence?.title || "Competitor Intelligence",
          description:
            dbContent.content_tools_access?.research_analysis
              ?.competitor_intelligence?.description ||
            "Study what's working for other honey and male enhancement brands. Updated weekly with new findings.",
          comingSoon:
            dbContent.content_tools_access?.research_analysis
              ?.competitor_intelligence?.coming_soon ?? true,
        },
      },
    },
  };
};

const CreateProductModal = ({
  isOpen,
  onClose,
  onSuccess,
  editProduct = null,
  brandId = null,
  brandName = null,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    shortDescription: "",
    mainImageUrl: "",
    additionalImages: [],
    painPoints: [],
    rules: [],
    links: [],
    addToCurrentBrand: false,
    marketingContent: {
      productAssets: {
        productMockupsUrl: "",
        rawUgcContentEnabled: false,
        trainingDocumentsUrl: "",
      },
      topPerformingExamples: {
        topImageAds: [],
        topVideoAds: [],
        imageLibraryUrl: "",
        videoLibraryUrl: "",
        videoAdsEnabled: false,
      },
      highPerformanceCreatives: {
        videos: [],
        creativeLibraryUrl: "",
      },
      knowYourCustomer: {
        targetDemographics: [],
        customerPersona: {
          name: "",
          age: "",
          location: "",
          income: "",
          jobTitle: "",
          reality: "",
          fears: "",
          hook: "",
        },
      },
      painPointsGrid: [],
      powerClaims: [],
      contentToolsAccess: {
        recharmUgcLibrary: {
          enabled: false,
          instructions: {
            websiteUrl: "https://app.recharm.com/auth",
            email: "ched@mailinator.com",
            inboxUrl: "https://mailinator.com",
          },
          description:
            "Access to 150+ verified UGC clips from real customers. Content updated weekly with fresh testimonials and usage videos for male enhancement and energy benefits.",
        },
        researchAnalysis: {
          kaloDataTraining: {
            title: "Kalo Data Training",
            description:
              "Learn to analyze trending content and competitor strategies for male enhancement and natural energy supplement niches.",
            comingSoon: true,
          },
          competitorIntelligence: {
            title: "Competitor Intelligence",
            description:
              "Study what's working for other honey and male enhancement brands. Updated weekly with new findings.",
            comingSoon: true,
          },
        },
      },
    },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Image dimension recommendations
  const imageDimensions = {
    main: {
      recommended: "1200×900px",
      aspectRatio: "4:3",
      maxFileSize: "2MB",
      description:
        "Main product showcase image - displayed prominently on affiliate dashboard",
    },
    additional: {
      recommended: "800×600px",
      aspectRatio: "4:3",
      maxFileSize: "1MB",
      description:
        "Additional product images - shown in gallery view for affiliates",
    },
  };

  const resetForm = useCallback(() => {
    setFormData({
      name: "",
      description: "",
      shortDescription: "",
      mainImageUrl: "",
      additionalImages: [],
      painPoints: [],
      rules: [],
      links: [],
      addToCurrentBrand: !!brandId,
      marketingContent: {
        productAssets: {
          productMockupsUrl: "",
          rawUgcContentEnabled: false,
          trainingDocumentsUrl: "",
        },
        topPerformingExamples: {
          topImageAds: [],
          topVideoAds: [],
          imageLibraryUrl: "",
          videoLibraryUrl: "",
          videoAdsEnabled: false,
        },
        highPerformanceCreatives: {
          videos: [],
          creativeLibraryUrl: "",
        },
        knowYourCustomer: {
          targetDemographics: [],
          customerPersona: {
            name: "",
            age: "",
            location: "",
            income: "",
            jobTitle: "",
            reality: "",
            fears: "",
            hook: "",
          },
        },
        painPointsGrid: [],
        powerClaims: [],
        contentToolsAccess: {
          recharmUgcLibrary: {
            enabled: false,
            instructions: {
              websiteUrl: "https://app.recharm.com/auth",
              email: "ched@mailinator.com",
              inboxUrl: "https://mailinator.com",
            },
            description:
              "Access to 150+ verified UGC clips from real customers. Content updated weekly with fresh testimonials and usage videos for male enhancement and energy benefits.",
          },
          researchAnalysis: {
            kaloDataTraining: {
              title: "Kalo Data Training",
              description:
                "Learn to analyze trending content and competitor strategies for male enhancement and natural energy supplement niches.",
              comingSoon: true,
            },
            competitorIntelligence: {
              title: "Competitor Intelligence",
              description:
                "Study what's working for other honey and male enhancement brands. Updated weekly with new findings.",
              comingSoon: true,
            },
          },
        },
      },
    });
  }, [brandId]);

  const loadProductData = async (productId) => {
    try {
      const result = await productService.getProductDetails(productId);
      if (result.success) {
        const product = result.data.product;
        const transformedContent = transformMarketingContentFromDb(
          result.data.marketing_content
        );

        setFormData({
          name: product.name,
          description: product.description,
          shortDescription: product.short_description || "",
          mainImageUrl: product.main_image_url || "",
          additionalImages: product.additional_images || [],
          painPoints: result.data.pain_points || [],
          rules: result.data.rules || [],
          links: result.data.links || [],
          addToCurrentBrand: false, // Not relevant for edit mode
          marketingContent: transformedContent,
        });
      } else {
        toast.error(`Failed to load product: ${result.error}`);
      }
    } catch (error) {
      toast.error(`Error loading product: ${error.message}`);
    }
  };

  // Load product data when editing
  useEffect(() => {
    if (editProduct) {
      loadProductData(editProduct);
    } else {
      resetForm();
    }
  }, [editProduct, resetForm]);

  // Image upload handler
  const handleImageUpload = async (file, isMain = false, returnUrl = false) => {
    if (!file) {
      console.log("⚠️ No file selected for upload");
      return returnUrl ? null : undefined;
    }

    // Validate file size based on image type
    const maxSize = isMain ? 2 * 1024 * 1024 : 1 * 1024 * 1024; // 2MB for main, 1MB for additional
    if (file.size > maxSize) {
      const maxSizeMB = maxSize / (1024 * 1024);
      toast.error(`File size must be less than ${maxSizeMB}MB`);
      return returnUrl ? null : undefined;
    }

    console.log("📤 Starting image upload:", {
      fileName: file.name,
      fileSize: file.size,
      isMain,
      returnUrl,
    });

    setImageUploading(true);
    try {
      const result = await productService.uploadImage(file);
      console.log("📊 Image upload result:", result);

      if (result.success) {
        if (returnUrl) {
          // Return URL for marketing content usage
          console.log(
            "✅ Image uploaded for marketing content:",
            result.data.url
          );
          toast.success("Image uploaded successfully");
          return result.data.url;
        } else if (isMain) {
          console.log("✅ Main image uploaded:", result.data.url);
          setFormData((prev) => ({ ...prev, mainImageUrl: result.data.url }));
        } else {
          console.log("✅ Additional image uploaded:", result.data.url);
          setFormData((prev) => ({
            ...prev,
            additionalImages: [...prev.additionalImages, result.data.url],
          }));
        }
        toast.success("Image uploaded successfully");
      } else {
        console.error("❌ Image upload failed:", result.error);
        toast.error(`Image upload failed: ${result.error}`);
        return returnUrl ? null : undefined;
      }
    } catch (error) {
      console.error("❌ Error uploading image:", error);
      toast.error(`Error uploading image: ${error.message}`);
      return returnUrl ? null : undefined;
    } finally {
      setImageUploading(false);
    }
  };

  // Drag and drop handlers for image reordering
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === targetIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newImages = [...formData.additionalImages];
    const draggedImage = newImages[draggedIndex];

    // Remove dragged item
    newImages.splice(draggedIndex, 1);

    // Insert at target position
    newImages.splice(targetIndex, 0, draggedImage);

    setFormData((prev) => ({ ...prev, additionalImages: newImages }));
    setDraggedIndex(null);
    setDragOverIndex(null);

    toast.success("Image order updated");
  };

  const removeAdditionalImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      additionalImages: prev.additionalImages.filter((_, i) => i !== index),
    }));
  };

  // Pain points handlers
  const addPainPoint = () => {
    setFormData((prev) => ({
      ...prev,
      painPoints: [
        ...prev.painPoints,
        { painPoint: "", psychologicalTrigger: "", adApplication: "" },
      ],
    }));
  };

  const removePainPoint = (index) => {
    setFormData((prev) => ({
      ...prev,
      painPoints: prev.painPoints.filter((_, i) => i !== index),
    }));
  };

  // Rules handlers
  const addRule = () => {
    setFormData((prev) => ({
      ...prev,
      rules: [...prev.rules, { ruleText: "" }],
    }));
  };

  const removeRule = (index) => {
    setFormData((prev) => ({
      ...prev,
      rules: prev.rules.filter((_, i) => i !== index),
    }));
  };

  // Links handlers
  const addLink = () => {
    setFormData((prev) => ({
      ...prev,
      links: [...prev.links, { title: "", url: "" }],
    }));
  };

  const removeLink = (index) => {
    setFormData((prev) => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== index),
    }));
  };

  // Marketing content handlers
  const addTopImageAd = () => {
    setFormData((prev) => ({
      ...prev,
      marketingContent: {
        ...prev.marketingContent,
        topPerformingExamples: {
          ...prev.marketingContent.topPerformingExamples,
          topImageAds: [
            ...prev.marketingContent.topPerformingExamples.topImageAds,
            { imageUrl: "", title: "", spend: 0, roas: 0 },
          ],
        },
      },
    }));
  };

  const removeTopImageAd = (index) => {
    setFormData((prev) => ({
      ...prev,
      marketingContent: {
        ...prev.marketingContent,
        topPerformingExamples: {
          ...prev.marketingContent.topPerformingExamples,
          topImageAds: prev.marketingContent.topPerformingExamples.topImageAds.filter(
            (_, i) => i !== index
          ),
        },
      },
    }));
  };

  const addTopVideoAd = () => {
    setFormData((prev) => ({
      ...prev,
      marketingContent: {
        ...prev.marketingContent,
        topPerformingExamples: {
          ...prev.marketingContent.topPerformingExamples,
          topVideoAds: [
            ...prev.marketingContent.topPerformingExamples.topVideoAds,
            { videoUrl: "", title: "", spend: 0, roas: 0 },
          ],
        },
      },
    }));
  };

  const removeTopVideoAd = (index) => {
    setFormData((prev) => ({
      ...prev,
      marketingContent: {
        ...prev.marketingContent,
        topPerformingExamples: {
          ...prev.marketingContent.topPerformingExamples,
          topVideoAds: prev.marketingContent.topPerformingExamples.topVideoAds.filter(
            (_, i) => i !== index
          ),
        },
      },
    }));
  };

  const addHighPerformanceVideo = () => {
    setFormData((prev) => ({
      ...prev,
      marketingContent: {
        ...prev.marketingContent,
        highPerformanceCreatives: {
          ...prev.marketingContent.highPerformanceCreatives,
          videos: [
            ...prev.marketingContent.highPerformanceCreatives.videos,
            {
              videoUrl: "",
              title: "",
              description: "",
              productionNotes: {
                aiVoice: false,
                ugcClips: false,
                bRoll: false,
                aiDeepFake: false,
                productBRoll: false,
                customNotes: [],
              },
            },
          ],
        },
      },
    }));
  };

  const removeHighPerformanceVideo = (index) => {
    setFormData((prev) => ({
      ...prev,
      marketingContent: {
        ...prev.marketingContent,
        highPerformanceCreatives: {
          ...prev.marketingContent.highPerformanceCreatives,
          videos: prev.marketingContent.highPerformanceCreatives.videos.filter(
            (_, i) => i !== index
          ),
        },
      },
    }));
  };

  const addTargetDemographic = () => {
    setFormData((prev) => ({
      ...prev,
      marketingContent: {
        ...prev.marketingContent,
        knowYourCustomer: {
          ...prev.marketingContent.knowYourCustomer,
          targetDemographics: [
            ...prev.marketingContent.knowYourCustomer.targetDemographics,
            "",
          ],
        },
      },
    }));
  };

  const removeTargetDemographic = (index) => {
    setFormData((prev) => ({
      ...prev,
      marketingContent: {
        ...prev.marketingContent,
        knowYourCustomer: {
          ...prev.marketingContent.knowYourCustomer,
          targetDemographics: prev.marketingContent.knowYourCustomer.targetDemographics.filter(
            (_, i) => i !== index
          ),
        },
      },
    }));
  };

  const addPainPointGrid = () => {
    setFormData((prev) => ({
      ...prev,
      marketingContent: {
        ...prev.marketingContent,
        painPointsGrid: [...prev.marketingContent.painPointsGrid, ""],
      },
    }));
  };

  const removePainPointGrid = (index) => {
    setFormData((prev) => ({
      ...prev,
      marketingContent: {
        ...prev.marketingContent,
        painPointsGrid: prev.marketingContent.painPointsGrid.filter(
          (_, i) => i !== index
        ),
      },
    }));
  };

  const addPowerClaim = () => {
    setFormData((prev) => ({
      ...prev,
      marketingContent: {
        ...prev.marketingContent,
        powerClaims: [...prev.marketingContent.powerClaims, ""],
      },
    }));
  };

  const removePowerClaim = (index) => {
    setFormData((prev) => ({
      ...prev,
      marketingContent: {
        ...prev.marketingContent,
        powerClaims: prev.marketingContent.powerClaims.filter(
          (_, i) => i !== index
        ),
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.description) {
      toast.error("Name and description are required");
      return;
    }

    setIsSubmitting(true);
    try {
      const result = editProduct
        ? await productService.updateProduct(editProduct, formData)
        : await productService.createProduct({
            ...formData,
            brandId: brandId,
          });

      if (result.success) {
        toast.success(
          `Product ${editProduct ? "updated" : "created"} successfully`
        );
        onSuccess();
        onClose();
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-white/10 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex justify-between items-center p-6 border-b border-white/10">
          <h2 className="text-2xl font-bold text-white">
            {editProduct ? "Edit Product" : "Create Product"}
          </h2>
          <button
            disabled={isSubmitting}
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Modal Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Branch Options - Only show for create modal when in brand context */}
          {!editProduct && brandId && brandName && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <div className="w-2 h-2 bg-[#B8460E] rounded-full"></div>
                Product Branch Options
              </h3>

              <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                <p className="text-sm text-gray-400 mb-4">
                  Choose whether this product should be added to the <span className="font-semibold text-green-400">{brandName}</span> brand or remain independent.
                </p>

                <div className="space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="branchOption"
                      value="current"
                      checked={formData.addToCurrentBrand === true}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          addToCurrentBrand: true,
                        }))
                      }
                      className="mt-1 w-4 h-4 text-[#B8460E] bg-white/5 border-white/20 focus:ring-[#B8460E] focus:ring-2"
                    />
                    <div>
                      <div className="text-white font-medium">
                        Add to <span className="font-semibold text-green-400">{brandName}</span> brand
                        <span className="ml-2 text-xs bg-[#B8460E]/20 text-[#B8460E] px-2 py-1 rounded">
                          Recommended
                        </span>
                      </div>
                      <div className="text-sm text-gray-400">
                        This product will be associated with the {brandName} brand and appear in its product catalog.
                      </div>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="branchOption"
                      value="independent"
                      checked={formData.addToCurrentBrand === false}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          addToCurrentBrand: false,
                        }))
                      }
                      className="mt-1 w-4 h-4 text-[#B8460E] bg-white/5 border-white/20 focus:ring-[#B8460E] focus:ring-2"
                    />
                    <div>
                      <div className="text-white font-medium">
                        Create as independent product
                      </div>
                      <div className="text-sm text-gray-400">
                        This product will not be associated with any specific brand and can be assigned to brands later.
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <div className="w-2 h-2 bg-[#B8460E] rounded-full"></div>
              Basic Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Product Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-[#B8460E] transition-colors"
                  placeholder="Enter product name"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Short Description
                </label>
                <input
                  type="text"
                  value={formData.shortDescription}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      shortDescription: e.target.value,
                    }))
                  }
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-[#B8460E] transition-colors"
                  placeholder="Brief description for listings"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Full Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-[#B8460E] transition-colors h-32 resize-none"
                placeholder="Detailed product description"
                required
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Images */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <div className="w-2 h-2 bg-[#B8460E] rounded-full"></div>
              Images
            </h3>

            {/* Main Image */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <label className="block text-sm font-medium text-gray-300">
                  Main Image
                </label>
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <Info className="w-3 h-3" />
                  <span>
                    {imageDimensions.main.recommended} •{" "}
                    {imageDimensions.main.aspectRatio} • Max{" "}
                    {imageDimensions.main.maxFileSize}
                  </span>
                </div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-lg p-3 mb-2">
                <p className="text-xs text-gray-400 mb-2">
                  {imageDimensions.main.description}
                </p>
                <div className="flex gap-4 items-center">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      handleImageUpload(e.target.files[0], true)
                    }
                    className="hidden"
                    id="main-image"
                    disabled={isSubmitting}
                  />
                  <label
                    htmlFor="main-image"
                    className="bg-[#B8460E]/20 hover:bg-[#B8460E]/30 text-[#B8460E] px-4 py-2 rounded-lg cursor-pointer transition-all duration-200 flex items-center gap-2"
                  >
                    <Upload className="w-4 h-4" />
                    {imageUploading ? "Uploading..." : "Upload Main Image"}
                  </label>
                  {formData.mainImageUrl && (
                    <img
                      src={formData.mainImageUrl}
                      alt="Main"
                      className="w-16 h-16 object-cover rounded-lg border-2 border-[#B8460E]/30"
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Additional Images */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <label className="block text-sm font-medium text-gray-300">
                  Additional Images
                </label>
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <Info className="w-3 h-3" />
                  <span>
                    {imageDimensions.additional.recommended} •{" "}
                    {imageDimensions.additional.aspectRatio} • Max{" "}
                    {imageDimensions.additional.maxFileSize}
                  </span>
                </div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-lg p-3 mb-2">
                <p className="text-xs text-gray-400 mb-3">
                  {imageDimensions.additional.description}
                </p>
                <div className="flex gap-4 items-center mb-4">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      handleImageUpload(e.target.files[0], false)
                    }
                    className="hidden"
                    id="additional-images"
                    disabled={isSubmitting}
                  />
                  <label
                    htmlFor="additional-images"
                    className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg cursor-pointer transition-all duration-200 flex items-center gap-2"
                  >
                    <Upload className="w-4 h-4" />
                    {imageUploading ? "Uploading..." : "Add Image"}
                  </label>
                </div>

                {formData.additionalImages.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3 text-xs text-gray-400">
                      <Move3D className="w-3 h-3" />
                      <span>Drag and drop to reorder images</span>
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      {formData.additionalImages.map((image, index) => (
                        <div
                          key={index}
                          className={`relative cursor-move transition-all duration-200 ${
                            draggedIndex === index
                              ? "opacity-50 scale-95"
                              : ""
                          } ${
                            dragOverIndex === index &&
                            draggedIndex !== index
                              ? "scale-105 ring-2 ring-[#B8460E]/50"
                              : ""
                          }`}
                          draggable
                          onDragStart={(e) => handleDragStart(e, index)}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, index)}
                          onDragEnd={() => {
                            setDraggedIndex(null);
                            setDragOverIndex(null);
                          }}
                        >
                          <div className="relative group">
                            <img
                              src={image}
                              alt={`Additional ${index + 1}`}
                              className="w-full h-20 object-cover rounded-lg"
                            />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                              <Move3D className="w-4 h-4 text-white" />
                            </div>
                            <div className="absolute -top-1 -left-1 bg-[#B8460E] text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                              {index + 1}
                            </div>
                            <button
                              type="button"
                              onClick={() => removeAdditionalImage(index)}
                              className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Pain Points */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <div className="w-2 h-2 bg-[#B8460E] rounded-full"></div>
                Pain Points & Triggers
              </h3>
              <button
                type="button"
                onClick={addPainPoint}
                disabled={isSubmitting}
                className="bg-[#B8460E]/20 hover:bg-[#B8460E]/30 text-[#B8460E] px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 disabled:opacity-50"
              >
                <Plus className="w-4 h-4" />
                Add Pain Point
              </button>
            </div>

            {formData.painPoints.map((point, index) => (
              <div
                key={index}
                className="bg-white/5 border border-white/10 rounded-lg p-4"
              >
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-white font-medium">
                    Pain Point #{index + 1}
                  </h4>
                  <button
                    type="button"
                    onClick={() => removePainPoint(index)}
                    disabled={isSubmitting}
                    className="text-red-400 hover:text-red-300 transition-colors disabled:opacity-50"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Pain Point
                    </label>
                    <input
                      type="text"
                      value={point.painPoint || ""}
                      onChange={(e) => {
                        const newPoints = [...formData.painPoints];
                        newPoints[index].painPoint = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          painPoints: newPoints,
                        }));
                      }}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-[#B8460E] transition-colors"
                      placeholder="Describe the pain point"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Psychological Trigger
                    </label>
                    <input
                      type="text"
                      value={point.psychologicalTrigger || ""}
                      onChange={(e) => {
                        const newPoints = [...formData.painPoints];
                        newPoints[index].psychologicalTrigger = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          painPoints: newPoints,
                        }));
                      }}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-[#B8460E] transition-colors"
                      placeholder="What psychological trigger does this address?"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Ad Application
                    </label>
                    <input
                      type="text"
                      value={point.adApplication || ""}
                      onChange={(e) => {
                        const newPoints = [...formData.painPoints];
                        newPoints[index].adApplication = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          painPoints: newPoints,
                        }));
                      }}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-[#B8460E] transition-colors"
                      placeholder="How to apply this in advertising"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Rules */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <div className="w-2 h-2 bg-[#B8460E] rounded-full"></div>
                Rules
              </h3>
              <button
                type="button"
                onClick={addRule}
                disabled={isSubmitting}
                className="bg-[#B8460E]/20 hover:bg-[#B8460E]/30 text-[#B8460E] px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 disabled:opacity-50"
              >
                <Plus className="w-4 h-4" />
                Add Rule
              </button>
            </div>

            {formData.rules.map((rule, index) => (
              <div
                key={index}
                className="bg-white/5 border border-white/10 rounded-lg p-4"
              >
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-white font-medium">
                    Rule #{index + 1}
                  </h4>
                  <button
                    type="button"
                    onClick={() => removeRule(index)}
                    disabled={isSubmitting}
                    className="text-red-400 hover:text-red-300 transition-colors disabled:opacity-50"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <textarea
                  value={rule.ruleText || ""}
                  onChange={(e) => {
                    const newRules = [...formData.rules];
                    newRules[index].ruleText = e.target.value;
                    setFormData((prev) => ({ ...prev, rules: newRules }));
                  }}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-[#B8460E] transition-colors h-20 resize-none"
                  placeholder="Enter rule text"
                  disabled={isSubmitting}
                />
              </div>
            ))}
          </div>

          {/* Links */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <div className="w-2 h-2 bg-[#B8460E] rounded-full"></div>
                Links
              </h3>
              <button
                type="button"
                onClick={addLink}
                disabled={isSubmitting}
                className="bg-[#B8460E]/20 hover:bg-[#B8460E]/30 text-[#B8460E] px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 disabled:opacity-50"
              >
                <Plus className="w-4 h-4" />
                Add Link
              </button>
            </div>

            {formData.links.map((link, index) => (
              <div
                key={index}
                className="bg-white/5 border border-white/10 rounded-lg p-4"
              >
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-white font-medium">
                    Link #{index + 1}
                  </h4>
                  <button
                    type="button"
                    onClick={() => removeLink(index)}
                    disabled={isSubmitting}
                    className="text-red-400 hover:text-red-300 transition-colors disabled:opacity-50"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      URL
                    </label>
                    <input
                      type="url"
                      value={link.url || ""}
                      onChange={(e) => {
                        const newLinks = [...formData.links];
                        newLinks[index].url = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          links: newLinks,
                        }));
                      }}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-[#B8460E] transition-colors"
                      placeholder="https://example.com"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Button Text
                    </label>
                    <input
                      type="text"
                      value={link.title || ""}
                      onChange={(e) => {
                        const newLinks = [...formData.links];
                        newLinks[index].title = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          links: newLinks,
                        }));
                      }}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-[#B8460E] transition-colors"
                      placeholder="Google Drive Link"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Marketing Content Section */}
          <div className="space-y-6 border-t border-white/10 pt-6">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <div className="w-2 h-2 bg-[#B8460E] rounded-full"></div>
              Marketing Content (Optional)
            </h3>
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="space-y-2">
                  <h4 className="text-blue-400 font-medium text-sm">
                    How Images Work
                  </h4>
                  <div className="text-xs text-blue-200 space-y-1">
                    <p>
                      <strong>Individual Images:</strong> Use the "Upload"
                      button to upload images directly, or paste image
                      URLs/filenames.
                    </p>
                    <p>
                      <strong>Image Library URLs:</strong> Link to Google
                      Drive folders containing your full image collections
                      for affiliates to browse.
                    </p>
                    <p>
                      <strong>Uploaded images:</strong> Stored in Supabase
                      and automatically get secure URLs.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Product Assets Section */}
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <h4 className="text-md font-semibold text-white mb-4 flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-[#B8460E] rounded-full"></div>
                Product Assets
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Product Mockups URL
                  </label>
                  <input
                    type="url"
                    value={
                      formData.marketingContent?.productAssets
                        ?.productMockupsUrl || ""
                    }
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        marketingContent: {
                          ...prev.marketingContent,
                          productAssets: {
                            ...prev.marketingContent.productAssets,
                            productMockupsUrl: e.target.value,
                          },
                        },
                      }))
                    }
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-[#B8460E] transition-colors"
                    placeholder="https://drive.google.com/..."
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Training Documents URL
                  </label>
                  <input
                    type="url"
                    value={
                      formData.marketingContent?.productAssets
                        ?.trainingDocumentsUrl || ""
                    }
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        marketingContent: {
                          ...prev.marketingContent,
                          productAssets: {
                            ...prev.marketingContent.productAssets,
                            trainingDocumentsUrl: e.target.value,
                          },
                        },
                      }))
                    }
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-[#B8460E] transition-colors"
                    placeholder="https://drive.google.com/..."
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={
                      formData.marketingContent?.productAssets
                        ?.rawUgcContentEnabled || false
                    }
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        marketingContent: {
                          ...prev.marketingContent,
                          productAssets: {
                            ...prev.marketingContent.productAssets,
                            rawUgcContentEnabled: e.target.checked,
                          },
                        },
                      }))
                    }
                    className="rounded border-white/10 bg-white/5 text-[#B8460E] focus:ring-[#B8460E]"
                    disabled={isSubmitting}
                  />
                  <span className="text-white text-sm">
                    Enable Raw UGC Content Access
                  </span>
                </label>
              </div>
            </div>

            {/* Top Performing Examples Section */}
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <h4 className="text-md font-semibold text-white mb-4 flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-[#B8460E] rounded-full"></div>
                Top Performing Examples
              </h4>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Image Library URL
                  </label>
                  <input
                    type="url"
                    value={
                      formData.marketingContent?.topPerformingExamples
                        ?.imageLibraryUrl || ""
                    }
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        marketingContent: {
                          ...prev.marketingContent,
                          topPerformingExamples: {
                            ...prev.marketingContent.topPerformingExamples,
                            imageLibraryUrl: e.target.value,
                          },
                        },
                      }))
                    }
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-[#B8460E] transition-colors"
                    placeholder="https://drive.google.com/..."
                    disabled={isSubmitting}
                  />
                </div>

                {/* Top Image Ads */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="block text-sm font-medium text-gray-300">
                      Top Image Ads
                    </label>
                    <button
                      type="button"
                      onClick={addTopImageAd}
                      disabled={isSubmitting}
                      className="bg-[#B8460E]/20 hover:bg-[#B8460E]/30 text-[#B8460E] px-3 py-1 rounded text-sm transition-all duration-200 flex items-center gap-1 disabled:opacity-50"
                    >
                      <Plus className="w-3 h-3" />
                      Add Image Ad
                    </button>
                  </div>

                  {formData.marketingContent?.topPerformingExamples?.topImageAds?.map((ad, index) => (
                    <div key={index} className="bg-white/5 border border-white/10 rounded-lg p-3 mb-3">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-white text-sm font-medium">Image Ad #{index + 1}</span>
                        <button
                          type="button"
                          onClick={() => removeTopImageAd(index)}
                          disabled={isSubmitting}
                          className="text-red-400 hover:text-red-300 transition-colors disabled:opacity-50"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <input
                            type="text"
                            value={ad.imageUrl || ""}
                            onChange={(e) => {
                              const newAds = [...formData.marketingContent.topPerformingExamples.topImageAds];
                              newAds[index].imageUrl = e.target.value;
                              setFormData((prev) => ({
                                ...prev,
                                marketingContent: {
                                  ...prev.marketingContent,
                                  topPerformingExamples: {
                                    ...prev.marketingContent.topPerformingExamples,
                                    topImageAds: newAds,
                                  },
                                },
                              }));
                            }}
                            className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-[#B8460E] transition-colors text-sm"
                            placeholder="Image URL"
                            disabled={isSubmitting}
                          />
                        </div>
                        <div>
                          <input
                            type="text"
                            value={ad.title || ""}
                            onChange={(e) => {
                              const newAds = [...formData.marketingContent.topPerformingExamples.topImageAds];
                              newAds[index].title = e.target.value;
                              setFormData((prev) => ({
                                ...prev,
                                marketingContent: {
                                  ...prev.marketingContent,
                                  topPerformingExamples: {
                                    ...prev.marketingContent.topPerformingExamples,
                                    topImageAds: newAds,
                                  },
                                },
                              }));
                            }}
                            className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-[#B8460E] transition-colors text-sm"
                            placeholder="Ad Title"
                            disabled={isSubmitting}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Know Your Customer Section */}
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <h4 className="text-md font-semibold text-white mb-4 flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-[#B8460E] rounded-full"></div>
                Know Your Customer
              </h4>

              {/* Target Demographics */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-sm font-medium text-gray-300">
                    Target Demographics
                  </label>
                  <button
                    type="button"
                    onClick={addTargetDemographic}
                    disabled={isSubmitting}
                    className="bg-[#B8460E]/20 hover:bg-[#B8460E]/30 text-[#B8460E] px-3 py-1 rounded text-sm transition-all duration-200 flex items-center gap-1 disabled:opacity-50"
                  >
                    <Plus className="w-3 h-3" />
                    Add Demographic
                  </button>
                </div>

                {formData.marketingContent?.knowYourCustomer?.targetDemographics?.map((demographic, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={demographic}
                      onChange={(e) => {
                        const newDemographics = [...formData.marketingContent.knowYourCustomer.targetDemographics];
                        newDemographics[index] = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          marketingContent: {
                            ...prev.marketingContent,
                            knowYourCustomer: {
                              ...prev.marketingContent.knowYourCustomer,
                              targetDemographics: newDemographics,
                            },
                          },
                        }));
                      }}
                      className="flex-1 bg-white/5 border border-white/10 rounded px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-[#B8460E] transition-colors text-sm"
                      placeholder="e.g., Men 25-45, Health-conscious individuals"
                      disabled={isSubmitting}
                    />
                    <button
                      type="button"
                      onClick={() => removeTargetDemographic(index)}
                      disabled={isSubmitting}
                      className="text-red-400 hover:text-red-300 transition-colors disabled:opacity-50"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Customer Persona */}
              <div>
                <h5 className="text-sm font-medium text-gray-300 mb-3">Customer Persona</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={formData.marketingContent?.knowYourCustomer?.customerPersona?.name || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        marketingContent: {
                          ...prev.marketingContent,
                          knowYourCustomer: {
                            ...prev.marketingContent.knowYourCustomer,
                            customerPersona: {
                              ...prev.marketingContent.knowYourCustomer.customerPersona,
                              name: e.target.value,
                            },
                          },
                        },
                      }))
                    }
                    className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-[#B8460E] transition-colors text-sm"
                    placeholder="Persona Name"
                    disabled={isSubmitting}
                  />
                  <input
                    type="text"
                    value={formData.marketingContent?.knowYourCustomer?.customerPersona?.age || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        marketingContent: {
                          ...prev.marketingContent,
                          knowYourCustomer: {
                            ...prev.marketingContent.knowYourCustomer,
                            customerPersona: {
                              ...prev.marketingContent.knowYourCustomer.customerPersona,
                              age: e.target.value,
                            },
                          },
                        },
                      }))
                    }
                    className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-[#B8460E] transition-colors text-sm"
                    placeholder="Age Range"
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-4 pt-6 border-t border-white/10">
            <button
              type="button"
              disabled={isSubmitting}
              onClick={onClose}
              className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all duration-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={imageUploading || isSubmitting}
              className="px-6 py-3 bg-gradient-to-r from-[#B8460E] to-[#8B3A0E] hover:from-[#8B3A0E] hover:to-[#B8460E] text-white rounded-lg transition-all duration-200 flex items-center gap-2 disabled:opacity-50"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {isSubmitting
                ? "Saving..."
                : editProduct
                  ? "Update Product"
                  : "Create Product"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProductModal;